import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "fribue_shop_cart";

// Une "ligne" de panier = un couple (produit, taille) unique - même produit
// dans deux tailles différentes = deux lignes, comme sur n'importe quelle
// boutique en ligne classique.
const lineKey = (productId, size) => `${productId}::${size || ""}`;

export function CartProvider({ children }) {
  const [lines, setLines] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {}
  }, [lines]);

  const addToCart = (product, size, quantity = 1) => {
    setLines((prev) => {
      const key = lineKey(product.product_id, size);
      const existing = prev.find((l) => lineKey(l.product_id, l.size) === key);
      if (existing) {
        return prev.map((l) => (lineKey(l.product_id, l.size) === key ? { ...l, quantity: l.quantity + quantity } : l));
      }
      return [
        ...prev,
        {
          product_id: product.product_id,
          name: product.name,
          image: product.images?.[0] || null,
          price_cents: product.price_cents,
          size: size || null,
          quantity,
        },
      ];
    });
  };

  const updateQuantity = (productId, size, quantity) => {
    setLines((prev) => {
      if (quantity <= 0) return prev.filter((l) => lineKey(l.product_id, l.size) !== lineKey(productId, size));
      return prev.map((l) => (lineKey(l.product_id, l.size) === lineKey(productId, size) ? { ...l, quantity } : l));
    });
  };

  const removeLine = (productId, size) => {
    setLines((prev) => prev.filter((l) => lineKey(l.product_id, l.size) !== lineKey(productId, size)));
  };

  const clearCart = () => setLines([]);

  const itemCount = lines.reduce((sum, l) => sum + l.quantity, 0);
  const totalCents = lines.reduce((sum, l) => sum + l.price_cents * l.quantity, 0);

  return (
    <CartContext.Provider value={{ lines, addToCart, updateQuantity, removeLine, clearCart, itemCount, totalCents }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
